import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import cx from 'clsx';
import React from 'react';
import { NavLink } from 'react-router';
import { itemLinkClasses } from './classes';

interface SubmenuButtonProps {
  open: boolean;
  innerRef: React.Ref<HTMLButtonElement>;
}
interface SubmenuProps {
  /** Button component to display */
  button: (props: SubmenuButtonProps) => React.JSX.Element;
  /** Position of the menu relative to the button */
  position?: keyof typeof positionClasses;
  /** Tailwind class for the width of the menu */
  widthClass?: string;
  children: React.ReactNode;
}

/** Positions for the open menu relative to the button */
const positionClasses = {
  'bottom-left': 'left-0',
  'bottom-right': 'right-0',
};

/**
 * Render a submenu for the AuthenticatedNavBar
 *
 * Known bug: cannot navigate through menu items with assistive technology
 */
function Submenu({
  button,
  position = 'bottom-left',
  widthClass,
  children,
}: SubmenuProps) {
  const finalWidthClass = widthClass || 'w-fit';

  return (
    <Menu as="div" className="relative z-30 w-fit">
      {({ open }) => (
        <>
          <MenuButton as={React.Fragment}>
            <SubmenuButton open={open} button={button} />
          </MenuButton>
          <MenuItems
            className={cx(
              finalWidthClass,
              'absolute bg-white shadow-md',
              positionClasses[position]
            )}
          >
            {children}
          </MenuItems>
        </>
      )}
    </Menu>
  );
}

const SubmenuButton = React.forwardRef<
  HTMLButtonElement,
  { open: boolean; button: SubmenuProps['button'] }
>(({ open, button, ...props }, ref) => {
  const PassedButton = button;

  return <PassedButton innerRef={ref} open={open} {...props} />;
});
SubmenuButton.displayName = 'SubmenuButton';

function SubmenuItem(props: Omit<ItemLinkProps, 'active' | 'focus' | 'close'>) {
  return (
    <MenuItem>
      {(renderProps) => <ItemLink {...props} {...renderProps} />}
    </MenuItem>
  );
}

interface ItemLinkProps {
  href: string;
  active: boolean;
  focus: boolean;
  close: () => void;
  children: React.ReactNode;
}
const ItemLink = React.forwardRef<HTMLAnchorElement, ItemLinkProps>(
  ({ active, href, focus, close, children, ...props }, ref) => {
    const linkProps = {
      ref,
      onClick: close,
      ...props,
    };

    return (
      <NavLink
        to={href}
        {...linkProps}
        className={({ isActive }) => itemLinkClasses(focus, isActive)}
      >
        {children}
      </NavLink>
    );
  }
);
ItemLink.displayName = 'ItemLink';

export { Submenu, SubmenuItem };
export type { SubmenuButtonProps };
